/**
 * HOOK: useContacts (VERSION REFACTORISÉE)
 * Utilise le hook useCRUD générique avec transformation de données
 *
 * 🎯 Avant: 251 lignes | Après: 47 lignes | Réduction: 81%
 */

import { useCallback, useMemo } from 'react';
import { CONTACT_CONFIG } from '../config/entities/contact.config';
import { useUsers } from '../contexts/UsersContext';
import { generateSearchableFields } from '../lib/entity-config';
import { applyFiltersToQuery } from '../lib/supabase-filters';
import { buildSearchOrConditions } from '../lib/postgrestSearch';
import { fetchAllPages, fetchInChunks } from '../lib/supabase-paginate';
import { resolveSortColumn, CONTACTS_FILTER_VIEW_SORT_COLUMNS } from '../lib/sort-columns';
import type { Contact, CreateContactDto, UpdateContactDto } from '../types/contact';
import { useAuth } from './useAuth';
import { useCRUD } from './useCRUD';
import { useSupabase } from './useSupabase';
import { supabase as supabaseClient } from '../lib/supabase';

/**
 * Retour réunion 07/07 (E) — rollout sûr de la recherche n° BPI sur contacts.
 * `bpi_code` n'existe sur `contacts_filter_view` qu'APRÈS la migration 00151
 * (application DB = geste pilote). Tant qu'elle n'est pas appliquée, inclure
 * `bpi_code` dans un `.or(...ilike...)` renvoie 400 (Postgres 42703) et casse
 * TOUTE la recherche contacts. On sonde donc UNE fois la présence de la colonne
 * (résultat mis en cache) et on l'exclut des champs de recherche si absente :
 * même code correct avant (dégrade sans BPI) ET après (recherche BPI/BED active)
 * la migration — aucune fenêtre de recherche cassée pendant le déploiement.
 */
let bpiCodeInViewCache: boolean | null = null;
let bpiCodeInViewProbe: Promise<boolean> | null = null;
async function contactsViewHasBpiCode(): Promise<boolean> {
  if (bpiCodeInViewCache !== null) return bpiCodeInViewCache;
  if (!bpiCodeInViewProbe) {
    bpiCodeInViewProbe = (async () => {
      try {
        const { error } = await supabaseClient
          .from('contacts_filter_view')
          .select('bpi_code')
          .limit(1);
        bpiCodeInViewCache = !error;
        return bpiCodeInViewCache;
      } catch {
        // Réseau/erreur inattendue : dégrader sans BPI (jamais casser la
        // recherche contacts). Ne pas mettre en cache → nouvelle tentative plus tard.
        bpiCodeInViewProbe = null;
        return false;
      }
    })();
  }
  return bpiCodeInViewProbe;
}

/** Retire `bpi_code` des champs de recherche si la colonne n'est pas (encore) en base. */
async function resolveContactSearchFields(fields: readonly string[]): Promise<string[]> {
  const hasBpi = await contactsViewHasBpiCode();
  return hasBpi ? [...fields] : fields.filter((f) => f !== 'bpi_code');
}

/**
 * Type brut retourné par Supabase avec les relations
 */
interface ContactRaw {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  birth_date?: string;
  origin_id?: string;
  origin?: {
    id: string;
    name: string;
    label: string;
  };
  school_id?: string;
  school?: {
    id: string;
    name: string;
  };
  notes?: string;
  status: string;
  assigned_to?: string;
  // Adresse
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  // Statut étudiant
  is_not_student?: boolean;
  socio_professional_category?: string;
  // Newsletter
  newsletter_opt_in?: boolean;
  // Délivrabilité email (TICKET-CRM-13)
  email_invalid_at?: string;
  email_invalid_reason?: string;
  created_at: string;
  updated_at: string;
  products?: Array<{
    product: {
      id: string;
      name: string;
    };
  }>;
  // Retour 07/07 (E) : n° BPI issu de contacts_filter_view (migration 00151)
  bpi_code?: string | null;
  // Date du dernier appel (contacts_filter_view, migration 00174)
  last_call_at?: string | null;
}

interface UseContactsOptions {
  /**
   * Mode de chargement
   * - 'client': Charge toutes les données au montage (défaut)
   * - 'server': Ne charge pas au montage (utiliser fetchPaginated)
   */
  mode?: 'client' | 'server';
}

export function useContacts(options: UseContactsOptions = {}) {
  const { mode = 'client' } = options;
  const supabase = useSupabase();
  const { user } = useAuth();
  const { usersMap } = useUsers(); // ✅ Cache des utilisateurs

  // Memoïser les searchableFields pour éviter les re-renders
  const searchableFields = useMemo(() => generateSearchableFields(CONTACT_CONFIG), []);

  const crud = useCRUD<Contact, CreateContactDto, UpdateContactDto, ContactRaw>('contacts', {
    select: `
      *,
      origin:origins(id, name, label),
      school:schools(id, name),
      products:contact_products(
        product:products(id, name)
      )
    `,
    orderBy: { column: 'created_at', ascending: false },
    manual: mode === 'server',
    searchableFields,

    // Transformer les données brutes en Contact typé
    transform: (raw): Contact => ({
      id: raw.id,
      first_name: raw.first_name,
      last_name: raw.last_name,
      email: raw.email,
      phone: raw.phone || null,
      birth_date: raw.birth_date || null,
      origin_id: raw.origin_id || null,
      origin: raw.origin || null,
      school_id: raw.school_id || null,
      school: raw.school || null,
      notes: raw.notes || null,
      status: raw.status as any,
      assigned_to: raw.assigned_to || null,
      // Adresse
      address: raw.address || null,
      city: raw.city || null,
      postal_code: raw.postal_code || null,
      country: raw.country || null,
      // Statut étudiant
      is_not_student: raw.is_not_student || false,
      socio_professional_category: raw.socio_professional_category || null,
      // Newsletter
      newsletter_opt_in: raw.newsletter_opt_in ?? true,
      // Délivrabilité email (TICKET-CRM-13)
      email_invalid_at: raw.email_invalid_at || null,
      email_invalid_reason: (raw.email_invalid_reason as Contact['email_invalid_reason']) || null,
      created_at: raw.created_at,
      updated_at: raw.updated_at,
      // Aplatir la structure des produits
      products: raw.products?.map((cp) => cp.product).filter(Boolean) || [],
      // assigned_user et call_count seront enrichis après le fetch
      assigned_user: null,
      call_count: 0,
      last_call_at: raw.last_call_at ?? null,
    }),

    // Callback après le fetch pour enrichir les contacts
    afterFetch: async (contacts) => {
      if (contacts.length === 0) return contacts;

      // Récupérer tous les appels pour compter
      const contactIds = contacts.map((c) => c.id);
      const callEvents = await fetchInChunks<any>(contactIds, 500, async (chunk) => {
        const { data, error } = await supabase
          .from('timeline_events')
          .select('entity_id')
          .eq('entity_type', 'contact')
          .eq('event_type', 'call_logged')
          .eq('metadata->>outcome', 'answered')
          .in('entity_id', chunk);
        if (error) throw error;
        return data || [];
      });

      // Compter les appels par contact
      const callCountMap = new Map<string, number>();
      for (const call of callEvents) {
        const count = callCountMap.get(call.entity_id) || 0;
        callCountMap.set(call.entity_id, count + 1);
      }

      // Enrichir les contacts avec assigned_user et call_count
      return contacts.map((contact) => ({
        ...contact,
        // Enrichir avec l'utilisateur assigné depuis le cache
        assigned_user:
          contact.assigned_to && usersMap[contact.assigned_to]
            ? usersMap[contact.assigned_to]
            : null,
        // Enrichir avec le nombre d'appels
        call_count: callCountMap.get(contact.id) || 0,
      }));
    },

    // Transformer l'input pour gérer les product_ids
    transformInput: (input) => {
      const { product_ids: _product_ids, ...rest } = input as any;
      return rest; // Les product_ids seront gérés séparément
    },

    // Activer le logging automatique
    enableAutoLogging: true,
    entityType: 'contact',
    getUserId: () => user?.id,
  });

  /**
   * Enrichir un contact avec les données de l'utilisateur assigné
   * Utilise le cache du UsersContext (pas de requête réseau)
   */
  async function enrichWithAssignedUser(contact: Contact): Promise<Contact> {
    if (!contact.assigned_to) return contact;

    // Utiliser le cache du UsersContext (O(1) lookup)
    const assignedUser = usersMap[contact.assigned_to];
    if (assignedUser) {
      return {
        ...contact,
        assigned_user: {
          id: assignedUser.id,
          email: assignedUser.email,
          full_name: assignedUser.full_name || null,
        },
      };
    }

    return contact;
  }

  /**
   * Récupérer un contact par ID avec l'utilisateur assigné
   */
  async function getContactById(id: string): Promise<Contact | null> {
    const contact = await crud.getById(id);
    if (!contact) return null;
    return enrichWithAssignedUser(contact);
  }

  /**
   * Créer un contact avec ses produits associés
   * Override de la méthode create pour gérer la relation many-to-many
   */
  async function createContact(dto: CreateContactDto): Promise<boolean> {
    try {
      const { product_ids, ...contactData } = dto;

      // Créer le contact
      const newContact = await crud.create(contactData as any);
      if (!newContact) return false;

      // Associer les produits si fournis
      if (product_ids && product_ids.length > 0) {
        const contactProducts = product_ids.map((product_id) => ({
          contact_id: newContact.id,
          product_id,
        }));

        const { error: linkError } = await supabase
          .from('contact_products')
          .insert(contactProducts);

        if (linkError) throw linkError;
      }

      // Recharger les données
      await crud.refresh();
      return true;
    } catch (err) {
      console.error('Error creating contact:', err);
      return false;
    }
  }

  /**
   * Mettre à jour un contact avec ses produits associés
   * Le logging est géré automatiquement par useCRUD
   */
  async function updateContact(id: string, dto: UpdateContactDto): Promise<boolean> {
    try {
      const { product_ids, ...contactData } = dto;

      // Mettre à jour les informations du contact (le logging est automatique)
      if (Object.keys(contactData).length > 0) {
        const success = await crud.update(id, contactData as any);
        if (!success) return false;
      }

      // Gérer les produits associés si fournis
      if (product_ids !== undefined) {
        // Supprimer toutes les associations existantes
        const { error: deleteError } = await supabase
          .from('contact_products')
          .delete()
          .eq('contact_id', id);

        if (deleteError) throw deleteError;

        // Recréer les associations
        if (product_ids.length > 0) {
          const contactProducts = product_ids.map((product_id) => ({
            contact_id: id,
            product_id,
          }));

          const { error: linkError } = await supabase
            .from('contact_products')
            .insert(contactProducts);

          if (linkError) throw linkError;
        }
      }

      // Recharger les données
      await crud.refresh();
      return true;
    } catch (err) {
      console.error('Error updating contact:', err);
      return false;
    }
  }

  /**
   * Retirer l'assignation d'un contact
   */
  async function unassignUser(id: string): Promise<boolean> {
    try {
      // Récupérer le contact actuel pour logger l'ancien utilisateur
      const contact = await getContactById(id);
      if (!contact) return false;

      const oldUserId = contact.assigned_to;
      const oldUserName = contact.assigned_user?.full_name || contact.assigned_user?.email || null;

      // Mettre à jour le contact
      const success = await crud.update(id, { assigned_to: null } as any);
      if (!success) return false;

      // Logger dans la timeline
      if (oldUserId) {
        try {
          await supabase.from('timeline_events').insert({
            entity_type: 'contact',
            entity_id: id,
            event_type: 'assigned_user_changed',
            title: 'Assignation retirée',
            description: `Assignation de ${oldUserName || 'utilisateur'} retirée`,
            created_by: user?.id || null,
            metadata: {
              old_user_id: oldUserId,
              old_user_name: oldUserName,
              new_user_id: null,
              new_user_name: null,
            },
          });
        } catch (logError) {
          console.error('Error logging timeline event:', logError);
        }
      }

      // Recharger les données
      await crud.refresh();
      return true;
    } catch (err) {
      console.error('Error unassigning user:', err);
      return false;
    }
  }

  /**
   * Récupérer TOUTES les données correspondant aux filtres (sans pagination)
   * Utilisé pour l'export CSV des données filtrées
   */
  const fetchFiltered = useCallback(
    async (params: import('../types/crud').FetchFilteredParams): Promise<Contact[]> => {
      try {
        const {
          search = '',
          filters,
          filterMode = 'and',
          requiredFilters,
          ids: requestedIds,
        } = params;

        let ids: string[];
        // ✨ Sprint 5 : Map pour conserver les données de la vue (crew_numbers, is_in_crew)
        let viewDataMap = new Map<string, any>();

        // ✅ Si des IDs spécifiques sont demandés (export sélection multi-pages)
        if (requestedIds && requestedIds.length > 0) {
          ids = requestedIds;
          // ✨ Sprint 5 : Charger crew_numbers et is_in_crew depuis la vue
          // Bug fix : chunker les IDs pour éviter de dépasser la limite PostgREST
          // (URL `id=in.(...)` > ~2-8KB → rejet silencieux). Cap empirique 500/chunk.
          // E (07/07) : bpi_code sélectionné seulement si la colonne existe
          // (rollout sûr pré-00151, cf. contactsViewHasBpiCode).
          const viewSelect = (await contactsViewHasBpiCode())
            ? 'id, crew_numbers, is_in_crew, bpi_code'
            : 'id, crew_numbers, is_in_crew';
          const viewData = await fetchInChunks<any>(requestedIds, 500, async (chunk) => {
            const { data, error } = await supabase
              .from('contacts_filter_view')
              .select(viewSelect)
              .in('id', chunk);
            if (error) throw error;
            return data || [];
          });
          if (viewData.length > 0) {
            viewDataMap = new Map(viewData.map((r: any) => [r.id, r]));
          }
        } else {
          // Séparer le filtre produit des autres filtres
          const productFilter = filters?.find((f: any) => f.field === 'product_id');
          const isInCrewFilter = filters?.find((f: any) => f.field === 'is_in_crew');
          const otherFilters =
            filters?.filter((f: any) => f.field !== 'product_id' && f.field !== 'is_in_crew') || [];

          // ✨ Sprint 5 : Toujours utiliser la vue pour avoir crew_numbers et is_in_crew
          const source = 'contacts_filter_view';

          // E (07/07) : résolu une fois hors du factory sync (bpi_code exclu si
          // la colonne n'est pas encore en base — cf. resolveContactSearchFields).
          const contactSearchFields = await resolveContactSearchFields(
            searchableFields as string[]
          );

          // R15 : factory de query (réinstancie à chaque page pour fetchAllPages)
          const buildQuery = () => {
            let query = supabase.from(source).select('*');

            // Appliquer le filtre produit manuellement (array contains)
            if (productFilter && productFilter.value) {
              query = query.contains('product_ids', [productFilter.value]);
            }

            // ✨ Sprint 5 : Appliquer le filtre is_in_crew
            if (isInCrewFilter && isInCrewFilter.value !== undefined) {
              const isInCrew = String(isInCrewFilter.value) === 'true';
              query = query.eq('is_in_crew', isInCrew);
            }

            // Recherche
            if (search && contactSearchFields.length > 0) {
              query = query.or(buildSearchOrConditions(contactSearchFields, search));
            }

            // Appliquer les autres filtres
            if (
              (otherFilters && otherFilters.length > 0) ||
              (requiredFilters && requiredFilters.length > 0)
            ) {
              query = applyFiltersToQuery(query, otherFilters, filterMode, requiredFilters || []);
            }

            // Tri par défaut
            return query.order('created_at', { ascending: false });
          };

          // R15 : pagination automatique pour contourner le cap PostgREST 1000
          const { data: rawData, error: fetchError } = await fetchAllPages<any>(buildQuery);

          if (fetchError) throw fetchError;

          // Récupérer les IDs pour enrichir avec relations
          ids = rawData?.map((r: any) => r.id) || [];
          // ✨ Sprint 5 : Conserver les données de la vue
          viewDataMap = new Map(rawData?.map((r: any) => [r.id, r]) || []);
        }

        // Enrichir les données avec les relations
        let enrichedData: any[] = [];
        if (ids.length > 0) {
          // Récupérer les contacts complets avec relations.
          // Bug fix : chunker pour éviter de dépasser la limite PostgREST (URL trop longue).
          const fullContacts = await fetchInChunks<any>(ids, 500, async (chunk) => {
            const { data, error } = await supabase
              .from('contacts')
              .select(
                `
                *,
                origin:origins(id, name, label),
                school:schools(id, name),
                products:contact_products(
                  product:products(id, name)
                )
              `
              )
              .in('id', chunk);
            if (error) throw error;
            return data || [];
          });

          if (fullContacts.length > 0) {
            // ✨ Sprint 5 : Merger avec les données de la vue
            const contactMap = new Map(
              fullContacts.map((c) => [
                c.id,
                {
                  ...c,
                  crew_numbers: viewDataMap.get(c.id)?.crew_numbers || [],
                  is_in_crew: viewDataMap.get(c.id)?.is_in_crew || false,
                  // E (07/07) : n° BPI issu de la vue (null tant que migration
                  // 00151 non appliquée) — sinon la colonne export CSV sort vide.
                  bpi_code: viewDataMap.get(c.id)?.bpi_code ?? null,
                  last_call_at: viewDataMap.get(c.id)?.last_call_at ?? null,
                },
              ])
            );
            enrichedData = ids.map((id: string) => contactMap.get(id)).filter(Boolean);
          }
        }

        // Transformer les données
        const transformedData = enrichedData.map((raw: any) => ({
          id: raw.id,
          first_name: raw.first_name,
          last_name: raw.last_name,
          email: raw.email,
          phone: raw.phone || null,
          birth_date: raw.birth_date || null,
          origin_id: raw.origin_id || null,
          origin: raw.origin || null,
          school_id: raw.school_id || null,
          school: raw.school || null,
          notes: raw.notes || null,
          status: raw.status,
          assigned_to: raw.assigned_to || null,
          address: raw.address || null,
          city: raw.city || null,
          postal_code: raw.postal_code || null,
          country: raw.country || null,
          is_not_student: raw.is_not_student || false,
          socio_professional_category: raw.socio_professional_category || null,
          newsletter_opt_in: raw.newsletter_opt_in ?? true,
          created_at: raw.created_at,
          updated_at: raw.updated_at,
          products: raw.products?.map((cp: any) => cp.product).filter(Boolean) || [],
          assigned_user:
            raw.assigned_to && usersMap[raw.assigned_to] ? usersMap[raw.assigned_to] : null,
          call_count: 0,
          last_call_at: raw.last_call_at ?? null,
          // ✨ Sprint 5 : Équipages
          crew_numbers: raw.crew_numbers || [],
          is_in_crew: raw.is_in_crew || false,
          // E (07/07) : n° BPI (colonne « N° BPI » contacts + export CSV)
          bpi_code: raw.bpi_code ?? null,
        }));

        // Enrichir avec le nombre d'appels
        if (transformedData.length > 0) {
          const contactIds = transformedData.map((c: any) => c.id);
          // Bug fix : chunker pour éviter de dépasser la limite PostgREST.
          const callEvents = await fetchInChunks<any>(contactIds, 500, async (chunk) => {
            const { data, error } = await supabase
              .from('timeline_events')
              .select('entity_id')
              .eq('entity_type', 'contact')
              .eq('event_type', 'call_logged')
              .eq('metadata->>outcome', 'answered')
              .in('entity_id', chunk);
            if (error) throw error;
            return data || [];
          });

          const callCountMap = new Map<string, number>();
          for (const call of callEvents) {
            const count = callCountMap.get(call.entity_id) || 0;
            callCountMap.set(call.entity_id, count + 1);
          }

          transformedData.forEach((contact: any) => {
            contact.call_count = callCountMap.get(contact.id) || 0;
          });
        }

        return transformedData as Contact[];
      } catch (error) {
        console.error('[fetchFiltered] Error:', error);
        throw error;
      }
    },
    [supabase, searchableFields, usersMap]
  );

  /**
   * Fetch paginé utilisant la vue contacts_filter_view
   * pour permettre le filtrage sur les produits (relation many-to-many)
   */
  const fetchPaginatedFromView = useCallback(
    async (params: any) => {
      try {
        const {
          page = 1,
          limit = 10,
          search = '',
          sort,
          filters,
          filterMode = 'and',
          requiredFilters,
        } = params;

        // Séparer les filtres spéciaux des autres filtres
        const productFilter = filters?.find((f: any) => f.field === 'product_id');
        const isInCrewFilter = filters?.find((f: any) => f.field === 'is_in_crew');
        const otherFilters =
          filters?.filter((f: any) => f.field !== 'product_id' && f.field !== 'is_in_crew') || [];

        // ✨ Sprint 5 : Toujours utiliser la vue pour avoir crew_numbers et is_in_crew
        const source = 'contacts_filter_view';

        // Construire la requête
        let query = supabase.from(source).select('*', { count: 'exact', head: false });

        // Appliquer le filtre produit manuellement (array contains)
        if (productFilter && productFilter.value) {
          // Utiliser .contains() pour filtrer les arrays UUID
          query = query.contains('product_ids', [productFilter.value]);
        }

        // ✨ Sprint 5 : Appliquer le filtre "fait partie d'un équipage"
        if (isInCrewFilter && isInCrewFilter.value !== undefined) {
          const isInCrew = String(isInCrewFilter.value) === 'true';
          query = query.eq('is_in_crew', isInCrew);
        }

        // Recherche (E 07/07 : bpi_code exclu si colonne pas encore en base)
        if (search && searchableFields && searchableFields.length > 0) {
          const contactSearchFields = await resolveContactSearchFields(
            searchableFields as string[]
          );
          query = query.or(buildSearchOrConditions(contactSearchFields, search));
        }

        // Appliquer les autres filtres
        if (
          (otherFilters && otherFilters.length > 0) ||
          (requiredFilters && requiredFilters.length > 0)
        ) {
          query = applyFiltersToQuery(query, otherFilters, filterMode, requiredFilters || []);
        }

        // Tri — whitelist serveur : un champ hors colonnes reelles de la vue
        // (ex: relation enrichie `assigned_user`) rabat sur created_at pour
        // eviter un 400 PostgREST (column does not exist).
        const sortColumn = resolveSortColumn(sort?.field, CONTACTS_FILTER_VIEW_SORT_COLUMNS);
        const sortAscending = sort?.direction === 'asc';
        query = query.order(sortColumn, { ascending: sortAscending });

        // Pagination
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);

        // Exécution
        const { data: rawData, error: fetchError, count } = await query;

        if (fetchError) throw fetchError;

        // Récupérer les IDs pour enrichir avec relations
        const ids = rawData?.map((r: any) => r.id) || [];

        // ✨ Sprint 5 : Créer une map des données de la vue pour conserver crew_numbers et is_in_crew
        const viewDataMap = new Map(rawData?.map((r: any) => [r.id, r]) || []);

        // Si on utilise la vue, on doit récupérer les relations séparément
        let enrichedData = rawData || [];
        if (ids.length > 0) {
          // Récupérer les contacts complets avec relations
          const { data: fullContacts } = await supabase
            .from('contacts')
            .select(
              `
              *,
              origin:origins(id, name, label),
              school:schools(id, name),
              products:contact_products(
                product:products(id, name)
              )
            `
            )
            .in('id', ids);

          if (fullContacts) {
            // Créer une map pour l'ordre et merger avec les données de la vue
            const contactMap = new Map(
              fullContacts.map((c) => [
                c.id,
                {
                  ...c,
                  // ✨ Sprint 5 : Conserver les champs de la vue
                  crew_numbers: viewDataMap.get(c.id)?.crew_numbers || [],
                  is_in_crew: viewDataMap.get(c.id)?.is_in_crew || false,
                  // E (07/07) : n° BPI issu de la vue (null tant que migration
                  // 00151 non appliquée) pour la colonne « N° BPI ».
                  bpi_code: viewDataMap.get(c.id)?.bpi_code ?? null,
                  last_call_at: viewDataMap.get(c.id)?.last_call_at ?? null,
                },
              ])
            );
            enrichedData = ids.map((id: string) => contactMap.get(id)).filter(Boolean);
          }
        }

        // Transformer les données
        const transformedData = enrichedData.map((raw: any) => ({
          id: raw.id,
          first_name: raw.first_name,
          last_name: raw.last_name,
          email: raw.email,
          phone: raw.phone || null,
          birth_date: raw.birth_date || null,
          origin_id: raw.origin_id || null,
          origin: raw.origin || null,
          school_id: raw.school_id || null,
          school: raw.school || null,
          notes: raw.notes || null,
          status: raw.status,
          assigned_to: raw.assigned_to || null,
          address: raw.address || null,
          city: raw.city || null,
          postal_code: raw.postal_code || null,
          country: raw.country || null,
          is_not_student: raw.is_not_student || false,
          socio_professional_category: raw.socio_professional_category || null,
          newsletter_opt_in: raw.newsletter_opt_in ?? true,
          created_at: raw.created_at,
          updated_at: raw.updated_at,
          products: raw.products?.map((cp: any) => cp.product).filter(Boolean) || [],
          assigned_user:
            raw.assigned_to && usersMap[raw.assigned_to] ? usersMap[raw.assigned_to] : null,
          call_count: 0, // Sera enrichi après
          last_call_at: raw.last_call_at ?? null,
          // ✨ Sprint 5 : Équipages
          crew_numbers: raw.crew_numbers || [],
          is_in_crew: raw.is_in_crew || false,
          // E (07/07) : n° BPI (colonne « N° BPI » contacts)
          bpi_code: raw.bpi_code ?? null,
        }));

        // Enrichir avec le nombre d'appels
        if (transformedData.length > 0) {
          const contactIds = transformedData.map((c: any) => c.id);
          const callEvents = await fetchInChunks<any>(contactIds, 500, async (chunk) => {
            const { data, error } = await supabase
              .from('timeline_events')
              .select('entity_id')
              .eq('entity_type', 'contact')
              .eq('event_type', 'call_logged')
              .eq('metadata->>outcome', 'answered')
              .in('entity_id', chunk);
            if (error) throw error;
            return data || [];
          });

          const callCountMap = new Map<string, number>();
          for (const call of callEvents) {
            const count = callCountMap.get(call.entity_id) || 0;
            callCountMap.set(call.entity_id, count + 1);
          }

          transformedData.forEach((contact: any) => {
            contact.call_count = callCountMap.get(contact.id) || 0;
          });
        }

        return {
          data: transformedData as Contact[],
          total: count || 0,
          totalPages: Math.ceil((count || 0) / limit),
          page,
          limit,
        };
      } catch (error) {
        console.error('[fetchPaginatedFromView] Error:', error);
        throw error;
      }
    },
    [supabase, searchableFields, usersMap]
  );

  /**
   * Récupérer tous les IDs correspondant aux filtres actuels (sans pagination)
   * Utilisé pour "Sélectionner tous les résultats" (Feature 4.4)
   */
  const fetchAllFilteredIds = useCallback(
    async (params: import('../types/crud').FetchFilteredParams): Promise<string[]> => {
      try {
        const { search = '', filters, filterMode = 'and', requiredFilters } = params;

        // Séparer le filtre produit des autres filtres
        const productFilter = filters?.find((f: any) => f.field === 'product_id');
        const otherFilters = filters?.filter((f: any) => f.field !== 'product_id') || [];

        // Retour 07/07 (E) : toujours lire contacts_filter_view (superset des
        // colonnes contacts + bpi_code/crew_numbers) pour que la sélection globale
        // « tout sélectionner » respecte une recherche n° BPI/BED, comme la liste.
        // Auparavant la source retombait sur `contacts` (sans bpi_code) hors filtre
        // produit → un `.or(bpi_code…)` aurait renvoyé 400.
        const source = 'contacts_filter_view';

        // E (07/07) : bpi_code exclu si colonne pas encore en base (rollout sûr).
        const contactSearchFields = await resolveContactSearchFields(searchableFields as string[]);

        // R15 : factory de query (réinstancie à chaque page pour fetchAllPages).
        // Récupère TOUS les IDs via pagination PostgREST (cap implicite = 1000) ;
        // sans cette boucle .range(), la sélection globale était silencieusement
        // tronquée à 1000 IDs (cf. bug 1500+ contacts non exportés).
        const buildQuery = () => {
          let query = supabase.from(source).select('id');

          // Appliquer le filtre produit manuellement (array contains)
          if (productFilter && productFilter.value) {
            query = query.contains('product_ids', [productFilter.value]);
          }

          // Recherche
          if (search && contactSearchFields.length > 0) {
            query = query.or(buildSearchOrConditions(contactSearchFields, search));
          }

          // Appliquer les autres filtres
          if (
            (otherFilters && otherFilters.length > 0) ||
            (requiredFilters && requiredFilters.length > 0)
          ) {
            query = applyFiltersToQuery(query, otherFilters, filterMode, requiredFilters || []);
          }

          return query;
        };

        const { data, error } = await fetchAllPages<{ id: string }>(buildQuery);

        if (error) throw error;

        return data?.map((item) => item.id) || [];
      } catch (error) {
        console.error('[fetchAllFilteredIds] Error:', error);
        throw error;
      }
    },
    [supabase, searchableFields]
  );

  return {
    // API spécifique (pour compatibilité)
    contacts: crud.data,
    loading: crud.loading,
    error: crud.error,
    refreshContacts: crud.refresh,
    getContactById, // Utilise la fonction enrichie
    createContact,
    updateContact,
    deleteContact: crud.remove,
    deleteContacts: crud.removeMany,
    unassignUser,

    // API générique (pour useEntityList)
    data: crud.data,
    allData: crud.data, // Toutes les données (pour Import CSV en mode server)
    create: createContact,
    update: updateContact,
    remove: crud.remove,
    removeMany: crud.removeMany,
    refresh: crud.refresh, // Charge TOUT en mode client
    refreshAll: crud.refresh, // Alias explicite pour charger tout
    fetchPaginated: fetchPaginatedFromView, // ✅ Utilise la vue pour supporter le filtre produit
    fetchFiltered, // ✅ Pour export CSV des données filtrées (sans pagination)
    fetchAllFilteredIds, // ✅ Pour sélection globale (Feature 4.4)
  };
}

/**
 * MIGRATION GUIDE:
 *
 * ✅ API 100% COMPATIBLE - Aucun changement nécessaire dans les composants !
 *
 * Le hook expose exactement les mêmes méthodes que l'ancienne version:
 * - contacts, loading, error
 * - fetchContacts(), getContactById(), createContact(), updateContact(), deleteContact(), deleteContacts()
 */
